import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { SolicitacaoFormData, Solicitacao } from '@/types/solicitacao';
import { useRequisicoes } from '@/hooks/useRequisicoes';
import { useCategorias } from '@/hooks/useCategorias';
import SolicitacaoFormFields from '../Solicitacoes/SolicitacaoFormFields';
import { FileUpload } from '@/components/ui/file-upload';
import { EditRequisicaoReadOnlyFields } from './EditRequisicaoReadOnlyFields';
import { EditRequisicaoDateFields } from './EditRequisicaoDateFields';
import { useEditRequisicaoFormLogic } from './EditRequisicaoFormLogic';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { useRequisicaoChat } from '@/hooks/useRequisicaoChat';
import { useRequisicaoLogs } from '@/hooks/useRequisicaoLogs';
import { useAuth } from '@/hooks/useAuth';
import { RequisicaoLogs } from "./RequisicaoLogs";
import { RequisicaoChat } from "./RequisicaoChat";
import { RequisicaoRegistrosRelacionados } from "./RequisicaoRegistrosRelacionados";
import { RequisicaoCIsEnvolvidos } from "./RequisicaoCIsEnvolvidos";
import { RequisicaoConhecimentoRelacionado } from "./RequisicaoConhecimentoRelacionado";
import { useRequisicaoParticipants } from "./useRequisicaoParticipants";
import { useUserGroupMembership } from "@/hooks/useUserGroupMembership";
import { formatCategoriaName } from '@/utils/formatters';
import { StatusSelect } from '@/components/common/StatusSelect';
import { TicketStatus } from '@/utils/statusTransitions';
import { AutoCloseIndicator } from './AutoCloseIndicator';
import { AlertOctagon } from 'lucide-react';
import NewProblemaDialog from '../Problemas/NewProblemaDialog';
import { buildProblemaFromOrigem } from '@/utils/promoteToProblema';

const requisicaoSchema = z.object({
  titulo: z.string().min(1, 'Título é obrigatório'),
  descricao: z.string().optional(),
  tipo: z.literal('requisicao'),
  categoria_id: z.string().optional(),
  sla_id: z.string().optional(),
  urgencia: z.enum(['baixa', 'media', 'alta', 'critica']),
  impacto: z.enum(['baixo', 'medio', 'alto']),
  prioridade: z.enum(['baixa', 'media', 'alta', 'critica']),
  status: z.enum(['aberta', 'em_andamento', 'pendente', 'resolvida', 'fechada', 'reaberta']),
  solicitante_id: z.string().optional(),
  client_id: z.string().optional(),
  grupo_responsavel_id: z.string().optional(),
  atendente_id: z.string().optional(),
  canal_origem: z.enum(['portal', 'email', 'telefone', 'chat', 'presencial']),
  data_limite_resposta: z.string().optional(),
  data_limite_resolucao: z.string().optional(),
  origem_id: z.string().optional(),
  ativos_envolvidos: z.array(z.any()).optional(),
  notas_internas: z.string().optional(),
  solucao_aplicada: z.string().optional(),
  gerar_kb: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
}).superRefine((data, ctx) => {
  // Atendente obrigatório para status em_andamento
  if (data.status === 'em_andamento' && (!data.atendente_id || data.atendente_id.trim() === '')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Atendente é obrigatório para iniciar o atendimento',
      path: ['atendente_id'],
    });
  }
  
  // Solução aplicada obrigatória para status resolvida, fechada ou reaberta
  if ((data.status === 'resolvida' || data.status === 'fechada' || data.status === 'reaberta') && 
      (!data.solucao_aplicada || data.solucao_aplicada.trim() === '')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Solução aplicada é obrigatória para resolver/fechar/reabrir o ticket',
      path: ['solucao_aplicada'],
    });
  }
});

interface EditRequisicaoDialogProps {
  requisicao: Solicitacao;
  isOpen: boolean;
  onClose: () => void;
}

type ChatMessage = {
  id: string;
  autor: 'analista' | 'cliente';
  texto: string;
  criadoEm: string;
};

export const EditRequisicaoDialog = ({ requisicao, isOpen, onClose }: EditRequisicaoDialogProps) => {
  const { updateRequisicao } = useRequisicoes();
  const { categorias } = useCategorias();
  const [anexos, setAnexos] = useState<string[]>([]);

  // Novo: obter usuário autenticado
  const { user } = useAuth();

  const form = useForm<SolicitacaoFormData>({
    resolver: zodResolver(requisicaoSchema),
    defaultValues: {
      titulo: '',
      tipo: 'requisicao',
      urgencia: 'media',
      impacto: 'medio',
      prioridade: 'media',
      status: 'aberta',
      canal_origem: 'portal',
    },
  });

  // Use the form logic hook
  useEditRequisicaoFormLogic({ form, requisicao });

  // Verificar se o usuário atual faz parte do grupo responsável
  const grupoResponsavelId = form.watch('grupo_responsavel_id');
  const { isUserInGroup } = useUserGroupMembership(grupoResponsavelId);
  
  // Determinar se os campos de grupo e SLA devem ficar read-only
  const shouldMakeGroupSlaReadOnly = grupoResponsavelId && !isUserInGroup;

  // Verificar se todos os campos devem ficar read-only baseado no status
  const currentStatus = form.watch('status');
  const isResolvedOrClosed = currentStatus === 'resolvida' || currentStatus === 'fechada' || currentStatus === 'reaberta';
  const shouldMakeAllFieldsReadOnly = currentStatus === 'resolvida' || currentStatus === 'fechada';
  
  // Status original para calcular transições permitidas
  const originalStatus = (requisicao.status || 'aberta') as TicketStatus;

  // Estado para Tabs
  const [tab, setTab] = useState("form");
  const [openPromote, setOpenPromote] = useState(false);
  const promote = buildProblemaFromOrigem(requisicao, 'requisicao');

  // Integração com chat e logs do Supabase
  const { chatMessages, isLoading: loadingChat, error: chatError, sendMessage } = useRequisicaoChat(requisicao.id);
  const { logs, isLoading: loadingLogs, error: logsError } = useRequisicaoLogs(requisicao.id);
  const [mensagem, setMensagem] = useState('');

  // Set anexos if they exist
  React.useEffect(() => {
    if (requisicao.anexos && Array.isArray(requisicao.anexos)) {
      const anexosUrls = requisicao.anexos.map((anexo) => anexo.url || anexo).filter(Boolean);
      setAnexos(anexosUrls);
    }
  }, [requisicao]);

  const onSubmit = async (data: SolicitacaoFormData) => {
    try {
      console.log('=== DEBUG: EditRequisicaoDialog onSubmit ===');
      console.log('Form data:', data);
      console.log('solucao_aplicada:', data.solucao_aplicada);
      console.log('gerar_kb:', data.gerar_kb);
      console.log('status:', data.status);
      
      // Adicionar anexos aos dados do formulário
      const dataWithAnexos = {
        ...data,
        anexos: anexos.length > 0 ? anexos.map(url => ({ url, type: 'file' })) : undefined,
      };

      console.log('Data with anexos:', dataWithAnexos);

      await updateRequisicao.mutateAsync({
        id: requisicao.id,
        data: dataWithAnexos,
      });
      
      console.log('Update successful!');
      form.reset();
      setAnexos([]);
      onClose();
    } catch (error) {
      console.error('Error updating requisição:', error);
    }
  };

  const handleClose = () => {
    form.reset();
    setAnexos([]);
    onClose();
  };

  // Chat envia mensagem: salva, bloqueia input até novo submit de outro autor
  const enviarMensagem = (autor: 'analista' | 'cliente') => {
    if (!mensagem.trim()) return;
    // setChatMsgs(msgs => [
    //   ...msgs, 
    //   { id: Math.random().toString(36).substring(2), autor, texto: mensagem.trim(), criadoEm: new Date().toLocaleString() }
    // ]);
    setMensagem('');
    // Simular que depois de 2s o outro lado libera para nova mensagem
    // setTimeout(() => setMensagemReadOnly(false), 2000);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex flex-col items-center justify-center w-full">
            <div className="flex w-full items-center justify-between mb-4 px-4">
              <DialogTitle className="text-center md:text-left flex-1 text-lg font-semibold">
                Editar Requisição - {requisicao.numero}
              </DialogTitle>
              <div className="flex items-center gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setOpenPromote(true)}
                  title="Abrir um Problema a partir desta requisição"
                >
                  <AlertOctagon className="h-4 w-4 mr-1" />
                  Abrir Problema
                </Button>
                <div className="min-w-[180px] flex justify-end">
                  <Form {...form}>
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem className="mb-0 w-full">
                          <FormLabel className="sr-only">Status</FormLabel>
                          <FormControl>
                            <StatusSelect
                              currentStatus={originalStatus}
                              value={field.value as TicketStatus}
                              onValueChange={field.onChange}
                              disabled={updateRequisicao.isPending}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </Form>
                </div>
              </div>
            </div>
            <AutoCloseIndicator 
              status={requisicao.status} 
              dataResolucao={requisicao.data_resolucao} 
            />
            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <TabsList className="w-full justify-center mb-2">
                <TabsTrigger value="form" className="flex-1">
                  Formulário
                </TabsTrigger>
                <TabsTrigger value="chat" className="flex-1">
                  Chat
                </TabsTrigger>
                <TabsTrigger value="logs" className="flex-1">
                  Logs
                </TabsTrigger>
                <TabsTrigger value="registros" className="flex-1">
                  Registros
                </TabsTrigger>
                <TabsTrigger value="cis" className="flex-1">
                  CIs
                </TabsTrigger>
                <TabsTrigger value="conhecimento" className="flex-1">
                  Conhecimento
                </TabsTrigger>
              </TabsList>
              <TabsContent value="form">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <EditRequisicaoReadOnlyFields form={form} readOnly={shouldMakeAllFieldsReadOnly} />
                    <SolicitacaoFormFields
                      form={form}
                      excludeFields={[
                        "titulo",
                        "descricao",
                        "data_limite_resposta",
                        "data_limite_resolucao",
                        "status",
                        ...(!isResolvedOrClosed ? ["solucao_aplicada"] : []),
                      ]}
                      readOnlyFields={shouldMakeAllFieldsReadOnly ? [
                        "categoria_id",
                        "sla_id", 
                        "urgencia",
                        "impacto",
                        "prioridade",
                        "solicitante_id",
                        "client_id",
                        "grupo_responsavel_id",
                        "atendente_id",
                        "canal_origem",
                        "notas_internas"
                      ] : []}
                      userSelectMode="searchable"
                      groupSlaReadOnly={shouldMakeGroupSlaReadOnly || shouldMakeAllFieldsReadOnly}
                      filteredCategorias={(categorias || []).filter(c => c.tipo === 'solicitacao' || c.tipo === 'requisicao')} 
                      slaAplicaA="solicitacao"
                    />
                    <EditRequisicaoDateFields form={form} readOnly={shouldMakeAllFieldsReadOnly} />
                    <FileUpload
                      onFilesChange={setAnexos}
                      maxFiles={5}
                      acceptedFileTypes="image/*,.pdf,.doc,.docx,.txt,.xlsx,.xls"
                      maxFileSize={10}
                      disabled={shouldMakeAllFieldsReadOnly}
                    />
                    <div className="flex justify-end space-x-2 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleClose}
                        disabled={updateRequisicao.isPending}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={updateRequisicao.isPending}
                      >
                        {updateRequisicao.isPending
                          ? "Atualizando..."
                          : "Atualizar Requisição"}
                      </Button>
                    </div>
                  </form>
                </Form>
              </TabsContent>
              <TabsContent value="chat">
                <RequisicaoChat requisicao={requisicao} />
              </TabsContent>
              <TabsContent value="logs">
                <RequisicaoLogs requisicao={requisicao} />
              </TabsContent>
              <TabsContent value="registros">
                <RequisicaoRegistrosRelacionados requisicao={requisicao} />
              </TabsContent>
              <TabsContent value="cis">
                <RequisicaoCIsEnvolvidos requisicao={requisicao} />
              </TabsContent>
              <TabsContent value="conhecimento">
                <RequisicaoConhecimentoRelacionado requisicao={requisicao} />
              </TabsContent>
            </Tabs>
          </div>
        </DialogHeader>
      </DialogContent>
      <NewProblemaDialog
        open={openPromote}
        onOpenChange={setOpenPromote}
        initialData={promote.initialData}
        origem={promote.origem}
      />
    </Dialog>
  );
};
